import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    TextField,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { ALL_USERS } from '../queries/GraphQLQueries';
import isEmail from 'validator/lib/isEmail';
import { useDialogNavigation } from '../hooks/useDialogNavigation';

const CREATE_NEW_USER = gql`
    mutation($givenName: String!, $familyName: String!, $displayName: String!, $email: String!,) {
        createUser(input: {
            givenName: $givenName
            familyName: $familyName
            displayName: $displayName
            email: $email
        }) {
            id
        }
    }
`;

export default function CreateUserView() {
    const handleCancel = useDialogNavigation();
    const emailDomain = process.env.REACT_APP_AUTHORIZED_EMAIL_DOMAIN;

    const [givenName, setGivenName] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState({});
    const [canSubmit, setCanSubmit] = useState(false);
    const [snackbar, setSnackbar] = useState(null);
    const handleCloseSnackbar = () => setSnackbar(null);

    const [createUserMutation, { loading: userLoading }] = useMutation(CREATE_NEW_USER, {
        refetchQueries: [
            { query: ALL_USERS }, 'GetUsers'
        ]
    });

    useEffect(() => {

        const canEnableSubmit = givenName !== ''
            && familyName !== ''
            && displayName !== ''
            && email !== ''
            && isEmail(generateCompleteEmailId(email));

        setCanSubmit(canEnableSubmit);

    }, [givenName, familyName, displayName, email]);

    function handleSubmit() {

        setSnackbar({ children: 'Creating new user...', severity: 'success' });
        setCanSubmit(false);

        createUserMutation({
            variables: {
                givenName: givenName,
                familyName: familyName,
                displayName: displayName,
                email: generateCompleteEmailId(email),
            }
        })
            .then(() => handleCancel())
            .catch((e) => {
                setSnackbar({ children: e?.message, severity: 'error' });
                setCanSubmit(true);
            });
    }

    function onEmailUpdate(email) {
        setEmail(email.toLowerCase());
        setEmailError(isEmail(generateCompleteEmailId(email)) ? {} : {error: true, helperText: 'Invalid Email'});
    }

    function generateCompleteEmailId(email) {
        return email + emailDomain;
    }

    return <>
        <Dialog open={true} onClose={handleCancel} disableRestoreFocus>
            <DialogTitle>Create New User</DialogTitle>
            <DialogContent>
                <DialogContentText>Enter the following details to create a new user.</DialogContentText>
                <Grid container direction="flex">
                    <Grid size={12}>
                        <TextField
                            autoFocus={true}
                            required={true}
                            margin='dense'
                            id='givenName'
                            label='Given Name'
                            type='text'
                            variant='standard'
                            value={givenName}
                            onChange={(e) => setGivenName(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            margin='dense'
                            required={true}
                            id='familyName'
                            label='Family Name'
                            type='text'
                            variant='standard'
                            value={familyName}
                            onChange={(e) => setFamilyName(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            margin='dense'
                            required={true}
                            id='displayName'
                            label='Display Name'
                            type='text'
                            variant='standard'
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={6}>
                        <TextField
                            {...emailError}
                            margin='dense'
                            required={true}
                            id='email'
                            label='Email Address'
                            type='text'
                            variant='standard'
                            value={email}
                            onChange={(e) => onEmailUpdate(e.target.value)}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Typography variant={"inherit"} color="textSecondary" style={{ paddingTop: 28 }}>{emailDomain}</Typography>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCancel} variant="contained">Cancel</Button>
                <Button onClick={handleSubmit} disabled={!canSubmit} variant="contained">Submit</Button>
            </DialogActions>
        </Dialog>
        {!!snackbar && (
            <Snackbar
                open
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={handleCloseSnackbar}
                autoHideDuration={2500}
            >
                <Alert {...snackbar} onClose={handleCloseSnackbar} />
            </Snackbar>
        )}
    </>
}
